import React, { useMemo, useState } from 'react';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from 'recharts';
import GenericTabs from '@ui-components/Tabs/GenericTabs';
import { GenericCard } from '@ui-components/Card/GenericCard';
import type { HistoryReport, AccountHistoryData } from '../hooks/useFortiFlexData';

interface AnalyticsTabProps {
    history: HistoryReport[];
    accountHistory: AccountHistoryData | null;
}

type RangeKey = '7d' | '30d' | '90d' | 'all';

const RANGE_TABS = [
    { label: 'Last 7d', value: '7d' as RangeKey },
    { label: 'Last 30d', value: '30d' as RangeKey },
    { label: 'Last 90d', value: '90d' as RangeKey },
    { label: 'All', value: 'all' as RangeKey },
];

const RANGE_DAYS: Record<RangeKey, number> = { '7d': 7, '30d': 30, '90d': 90, all: 9999 };

const COLORS = [
    '#005E69', '#00BCD1', '#FAAEB0', '#761E21',
    '#f59e0b', '#3b82f6', '#ff4089', '#5ce5d5',
];

function fmt(n: number | null | undefined): string {
    if (n === null || n === undefined || !Number.isFinite(n)) return 'Pending';
    return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

// Days Fortinet has not published yet arrive as null and must be skipped,
// never averaged in as zero.
function published(values: (number | null | undefined)[]): number[] {
    return values.filter((v): v is number => typeof v === 'number');
}

function filterByRange<T extends { reportDate?: string; date?: string }>(
    data: T[],
    range: RangeKey
): T[] {
    if (range === 'all') return data;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - RANGE_DAYS[range]);
    const cutoffStr = cutoff.toISOString().slice(0, 10);
    return data.filter((d) => (d.reportDate || d.date || '') >= cutoffStr);
}

function computeMovingAverage(
    data: { date: string; value: number | null }[],
    window: number
): { date: string; ma: number | null }[] {
    const result: { date: string; ma: number | null }[] = [];
    for (let i = 0; i < data.length; i++) {
        const start = Math.max(0, i - window + 1);
        const slice = published(data.slice(start, i + 1).map((d) => d.value));
        const avg = slice.length
            ? slice.reduce((s, v) => s + v, 0) / slice.length
            : null;
        result.push({
            date: data[i].date,
            ma: avg === null ? null : Math.round(avg * 100) / 100
        });
    }
    return result;
}

const AnalyticsTab: React.FC<AnalyticsTabProps> = ({ history, accountHistory }) => {
    const [range, setRange] = useState<RangeKey>('90d');

    const sortedHistory = useMemo(() => {
        const filtered = filterByRange(history, range);
        return [...filtered].sort(
            (a, b) => new Date(a.reportDate).getTime() - new Date(b.reportDate).getTime()
        );
    }, [history, range]);

    const monthlyHistory = useMemo(() => {
        if (!sortedHistory.length) return [];
        const byMonth = new Map<string, typeof sortedHistory[0]>();
        for (const entry of sortedHistory) {
            const key = entry.reportDate.slice(0, 7);
            byMonth.set(key, entry);
        }
        return Array.from(byMonth.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([month, entry]) => ({
                ...entry,
                month,
            }));
    }, [sortedHistory]);

    const filteredDailyTotals = useMemo(() => {
        if (!accountHistory?.dailyTotals) return [];
        return filterByRange(accountHistory.dailyTotals, range);
    }, [accountHistory, range]);

    const dailyKpis = useMemo(() => {
        const dailyValues = published(
            filteredDailyTotals.map((d) => d.burnRateDaily)
        );
        const totals = published(filteredDailyTotals.map((d) => d.totalDaily));
        return {
            avgDaily: dailyValues.length
                ? dailyValues.reduce((s, v) => s + v, 0) / dailyValues.length
                : null,
            peakDaily: dailyValues.length ? Math.max(...dailyValues) : null,
            minDaily: dailyValues.length ? Math.min(...dailyValues) : null,
            totalPeriod: totals.length
                ? totals.reduce((s, v) => s + v, 0)
                : null,
            pendingDays: filteredDailyTotals.length - dailyValues.length
        };
    }, [filteredDailyTotals]);

    const { avgDaily, peakDaily, minDaily, totalPeriod } = dailyKpis;

    const dailyWithMa = useMemo(() => {
        const raw = filteredDailyTotals.map((d) => ({ date: d.date, value: d.burnRateDaily }));
        const ma7 = computeMovingAverage(raw, 7);
        return filteredDailyTotals.map((d, i) => ({
            ...d,
            ma7: ma7[i]?.ma ?? d.burnRateDaily,
        }));
    }, [filteredDailyTotals]);

    const stackedData = useMemo(() => {
        if (!accountHistory?.accounts?.length || !accountHistory.dailyTotals?.length) return [];
        const accts = accountHistory.accounts;
        const dailyMap = new Map<string, Record<string, number>>();

        for (const acct of accts) {
            for (const pt of acct.series) {
                if (!dailyMap.has(pt.date)) {
                    dailyMap.set(pt.date, { date: pt.date as unknown as number } as Record<string, number>);
                }
                const row = dailyMap.get(pt.date);
                if (row) row[acct.label] = pt.daily;
            }
        }

        const allDates = Array.from(dailyMap.keys()).sort();
        const cutoff = range === 'all' ? '' : (() => {
            const c = new Date();
            c.setDate(c.getDate() - RANGE_DAYS[range]);
            return c.toISOString().slice(0, 10);
        })();

        return allDates
            .filter((d) => d >= cutoff)
            .map((d) => dailyMap.get(d) as Record<string, number>);
    }, [accountHistory, range]);

    const accountNames = useMemo(() => {
        return accountHistory?.accounts?.map((a) => a.label) || [];
    }, [accountHistory]);

    const cumulativeStackedData = useMemo(() => {
        if (!accountHistory?.accounts?.length) return [];
        const accts = accountHistory.accounts;
        const dailyMap = new Map<string, Record<string, number>>();

        for (const acct of accts) {
            for (const pt of acct.series) {
                if (!dailyMap.has(pt.date)) {
                    dailyMap.set(pt.date, {} as Record<string, number>);
                }
                const row = dailyMap.get(pt.date);
                if (row) row[acct.label] = pt.cumulative;
            }
        }

        const allDates = Array.from(dailyMap.keys()).sort();
        const cutoff = range === 'all' ? '' : (() => {
            const c = new Date();
            c.setDate(c.getDate() - RANGE_DAYS[range]);
            return c.toISOString().slice(0, 10);
        })();

        return allDates
            .filter((d) => d >= cutoff)
            .map((d) => ({ date: d, ...(dailyMap.get(d) ?? {}) }));
    }, [accountHistory, range]);

    const pctData = useMemo(() => {
        return sortedHistory.map((r) => ({
            date: r.reportDate,
            pctConsumed: r.pointsTotal > 0
                ? Math.round((r.pointsUsed / r.pointsTotal) * 1000) / 10
                : 0,
        }));
    }, [sortedHistory]);

    if (!history.length) {
        return (
            <p className="text-sm text-semantic-text-secondary py-8 text-center">
                No historical data available. Reports will accumulate over time.
            </p>
        );
    }

    return (
        <div className="space-y-4">
            <GenericTabs
                tabs={RANGE_TABS}
                activeTab={range}
                onTabChange={(v) => setRange(v as RangeKey)}
                size="sm"
            />

            {/* KPI row */}
            {filteredDailyTotals.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                        {
                            label: 'Avg Daily',
                            value: fmt(avgDaily),
                            unit: 'pts/day',
                        },
                        {
                            label: 'Peak Daily',
                            value: fmt(peakDaily),
                            unit: 'pts/day',
                        },
                        {
                            label: 'Min Daily',
                            value: fmt(minDaily),
                            unit: 'pts/day',
                        },
                        {
                            label: 'Total Period',
                            value: fmt(totalPeriod),
                            unit: 'pts consumed',
                        },
                    ].map((kpi) => (
                        <GenericCard
                            key={kpi.label}
                            isCollapsible={false}
                            variant="primary"
                            bodyContent={
                                <div className="text-center">
                                    <span className="text-xs font-medium text-semantic-text-secondary uppercase tracking-wider">
                                        {kpi.label}
                                    </span>
                                    <p className="text-lg font-bold text-semantic-text-primary mt-1">
                                        {kpi.value}
                                    </p>
                                    <span className="text-xs text-semantic-text-secondary">{kpi.unit}</span>
                                </div>
                            }
                        />
                    ))}
                </div>
            )}

            {/* Points Over Time */}
            <GenericCard
                isCollapsible={false}
                variant="primary"
                headerContent={
                    <h3 className="text-sm font-semibold text-semantic-text-primary">
                        Points Over Time
                    </h3>
                }
                bodyContent={
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={monthlyHistory}>
                            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip
                                formatter={(v: number) => fmt(v)}
                                labelFormatter={(label: string) => `Month: ${label}`}
                            />
                            <Legend />
                            <Bar
                                dataKey="pointsUsed"
                                name="Used"
                                fill="#761E21"
                                stackId="points"
                            />
                            <Bar
                                dataKey="pointsRemaining"
                                name="Remaining"
                                fill="#005E69"
                                stackId="points"
                                radius={[3, 3, 0, 0]}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                }
            />

            {/* Daily Consumption + 7-day MA */}
            {dailyWithMa.length > 0 && (
                <GenericCard
                    isCollapsible={false}
                    variant="primary"
                    headerContent={
                        <h3 className="text-sm font-semibold text-semantic-text-primary">
                            Daily Consumption + 7-Day Moving Average
                        </h3>
                    }
                    bodyContent={
                        <ResponsiveContainer width="100%" height={280}>
                            <LineChart data={dailyWithMa}>
                                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} />
                                <Tooltip formatter={(v: number | null) => fmt(v)} />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="burnRateDaily"
                                    name="Daily (pts)"
                                    stroke="#f59e0b"
                                    strokeWidth={1}
                                    dot={{ r: 2 }}
                                    opacity={0.6}
                                    connectNulls={false}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="ma7"
                                    name="7-Day Avg"
                                    stroke="#00BCD1"
                                    strokeWidth={2.5}
                                    dot={false}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    }
                />
            )}

            {/* Stacked Area: daily by account */}
            {stackedData.length > 0 && accountNames.length > 0 && (
                <GenericCard
                    isCollapsible={false}
                    variant="primary"
                    headerContent={
                        <h3 className="text-sm font-semibold text-semantic-text-primary">
                            Daily Consumption by Account
                        </h3>
                    }
                    bodyContent={
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={stackedData}>
                                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} />
                                <Tooltip formatter={(v: number) => `${fmt(v)} pts`} />
                                <Legend />
                                {accountNames.map((name, i) => (
                                    <Area
                                        key={name}
                                        type="monotone"
                                        dataKey={name}
                                        stackId="accounts"
                                        fill={COLORS[i % COLORS.length]}
                                        stroke={COLORS[i % COLORS.length]}
                                        fillOpacity={0.6}
                                    />
                                ))}
                            </AreaChart>
                        </ResponsiveContainer>
                    }
                />
            )}

            {/* Stacked Area: cumulative by account */}
            {cumulativeStackedData.length > 0 && accountNames.length > 0 && (
                <GenericCard
                    isCollapsible={false}
                    variant="primary"
                    headerContent={
                        <h3 className="text-sm font-semibold text-semantic-text-primary">
                            Cumulative Consumption by Account
                        </h3>
                    }
                    bodyContent={
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={cumulativeStackedData}>
                                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} />
                                <Tooltip formatter={(v: number) => `${fmt(v)} pts`} />
                                <Legend />
                                {accountNames.map((name, i) => (
                                    <Area
                                        key={name}
                                        type="monotone"
                                        dataKey={name}
                                        stackId="cumulative"
                                        fill={COLORS[i % COLORS.length]}
                                        stroke={COLORS[i % COLORS.length]}
                                        fillOpacity={0.5}
                                    />
                                ))}
                            </AreaChart>
                        </ResponsiveContainer>
                    }
                />
            )}

            {/* % Consumed Over Time */}
            {pctData.length > 0 && (
                <GenericCard
                    isCollapsible={false}
                    variant="primary"
                    headerContent={
                        <h3 className="text-sm font-semibold text-semantic-text-primary">
                            % Points Consumed Over Time
                        </h3>
                    }
                    bodyContent={
                        <ResponsiveContainer width="100%" height={220}>
                            <AreaChart data={pctData}>
                                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                <YAxis
                                    tick={{ fontSize: 11 }}
                                    domain={[0, 100]}
                                    tickFormatter={(v) => `${v}%`}
                                />
                                <Tooltip formatter={(v: number) => `${v}%`} />
                                <Area
                                    type="monotone"
                                    dataKey="pctConsumed"
                                    name="% Consumed"
                                    fill="#FAAEB0"
                                    stroke="#761E21"
                                    fillOpacity={0.15}
                                    strokeWidth={2}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    }
                />
            )}
        </div>
    );
};

export default AnalyticsTab;
